import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2, Circle, Sparkles, X, Trash2, Plus, Upload, Users, ArrowRight,
} from 'lucide-react';
import { assetsApi, usersApi, tenantSettingsApi } from '../services/api';

/**
 * OnboardingProgress — quick-start ribbon for the Dashboard.
 *
 * Auto-hides if either:
 *   • all four steps are complete, or
 *   • the user has explicitly dismissed it (stored in localStorage).
 *
 * Steps (data-driven so a fresh tenant sees the right next action):
 *   1. Remove demo data (only shown while is_demo records exist)
 *   2. Add your first real asset (non-demo)
 *   3. Import your fleet from a spreadsheet (CTA to /assets/import)
 *   4. Invite your team (more than just the founding admin)
 */
const DISMISS_KEY = 'guardian:onboarding_ribbon_dismissed';

const OnboardingProgress = () => {
  const navigate = useNavigate();
  const [hidden, setHidden] = useState(() => localStorage.getItem(DISMISS_KEY) === '1');
  const [state, setState] = useState({
    realAssets: 0,
    demoTotal: 0,
    users: 0,
    loading: true,
  });

  useEffect(() => {
    if (hidden) return;
    const load = async () => {
      try {
        const [assetsRes, demoRes, usersRes] = await Promise.all([
          assetsApi.getAll(),
          tenantSettingsApi.getDemoData(),
          usersApi.getAll(),
        ]);
        const assets = Array.isArray(assetsRes.data) ? assetsRes.data : [];
        // AssetResponse doesn't expose `is_demo`, so "real assets" =
        // total visible assets minus the count of demo assets the server
        // told us about in /tenant-settings/demo-data.
        const demoAssetCount = demoRes.data?.counts?.assets || 0;
        const realAssets = Math.max(0, assets.length - demoAssetCount);
        setState({
          realAssets,
          demoTotal: demoRes.data?.total || 0,
          users: (usersRes.data || []).length,
          loading: false,
        });
      } catch {
        setState((s) => ({ ...s, loading: false }));
      }
    };
    load();
  }, [hidden]);

  if (hidden || state.loading) return null;

  const steps = [
    {
      id: 'remove-demo',
      label: 'Remove the sample data',
      sub: state.demoTotal > 0 ? `${state.demoTotal} demo records currently in your workspace` : null,
      done: state.demoTotal === 0,
      icon: Trash2,
      onClick: () => navigate('/settings'),
      ctaLabel: 'Go to Profile →',
      visible: state.demoTotal > 0, // hide entirely once cleaned
    },
    {
      id: 'add-asset',
      label: 'Add your first real asset',
      sub: state.realAssets > 0 ? `${state.realAssets} real ${state.realAssets === 1 ? 'asset' : 'assets'} so far` : 'Or import a whole fleet at once',
      done: state.realAssets > 0,
      icon: Plus,
      onClick: () => navigate('/assets'),
      ctaLabel: 'Add an asset',
      visible: true,
    },
    {
      id: 'import-fleet',
      label: 'Bulk-import your fleet from a spreadsheet',
      sub: 'Up to 500 assets in one go',
      done: false, // never auto-checks — purely an offer
      icon: Upload,
      onClick: () => navigate('/assets/import'),
      ctaLabel: 'Open the importer',
      visible: true,
      optional: true,
    },
    {
      id: 'invite-team',
      label: 'Invite your team',
      sub: state.users > 1 ? `${state.users} team members so far` : 'Field & mobile users are always free',
      done: state.users > 1,
      icon: Users,
      onClick: () => navigate('/users'),
      ctaLabel: 'Invite users',
      visible: true,
    },
  ];

  const visibleSteps = steps.filter((s) => s.visible);
  const requiredSteps = visibleSteps.filter((s) => !s.optional);
  const completed = requiredSteps.filter((s) => s.done).length;
  const total = requiredSteps.length;

  if (total > 0 && completed >= total) {
    // All checklist work done — silently hide so the ribbon stops nagging
    return null;
  }

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, '1');
    setHidden(true);
  };

  return (
    <div
      className="rounded-xl border border-[#EFE0A0] bg-[#FFFCEF] p-5"
      data-testid="onboarding-progress"
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="flex items-start gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#FFD108] flex items-center justify-center shrink-0">
            <Sparkles className="w-5 h-5 text-black" />
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-[0.25em] text-[#FFD108] font-semibold">
              Getting started
            </p>
            <h2 className="text-lg font-bold leading-tight">
              {completed === 0
                ? 'Pick a quick win to make this yours'
                : `${completed} of ${total} done — keep going`}
            </h2>
          </div>
        </div>
        <button
          onClick={handleDismiss}
          className="text-muted-foreground hover:text-foreground transition-colors p-1"
          data-testid="onboarding-dismiss-btn"
          title="Dismiss"
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      {/* Steps */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {visibleSteps.map((s) => (
          <StepCard key={s.id} {...s} />
        ))}
      </div>
    </div>
  );
};

const StepCard = ({ id, label, sub, done, icon: Icon, onClick, ctaLabel, optional }) => (
  <button
    onClick={onClick}
    className={`text-left rounded-lg border p-3 transition-colors flex flex-col gap-2 ${
      done
        ? 'border-[#A7F3D0] bg-[#ECFDF5] hover:bg-[#DCFCE7]'
        : 'border-[#E2DED4] bg-white hover:border-[#EFD9A3] hover:bg-[#FFFCEF]'
    }`}
    data-testid={`onboarding-step-${id}`}
  >
    <div className="flex items-start gap-2">
      {done ? (
        <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
      ) : (
        <Circle className="w-4 h-4 text-muted-foreground shrink-0 mt-0.5" />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold leading-snug">
          {label}
          {optional && <span className="text-[10px] text-muted-foreground ml-1.5 font-normal">optional</span>}
        </p>
        {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      </div>
    </div>
    <div className="flex items-center justify-between mt-auto pt-1">
      <span className={`inline-flex items-center gap-1 text-xs font-medium ${done ? 'text-emerald-600' : 'text-[#FFD108]'}`}>
        <Icon className="w-3.5 h-3.5" />
        {done ? 'Complete' : ctaLabel}
      </span>
      {!done && <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />}
    </div>
  </button>
);

export default OnboardingProgress;
